
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';

import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';
import { ToastService } from '../../../shared/toast/toast.service';
import { ErrorService } from '../../../shared/services/error-service';
import { InitializerService } from '../../../shared/services/initializer-service';
import { MatchingService, PeriodService, ClientAreaService, ClientService, AllPeriodItem, CreateMatchingInput, MatchingCreateResult } from '../../../../service/proxy';

@Component({
    templateUrl: './create-matching-batch.component.html',
    styleUrls: ['./create-matching-batch.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent
    ],
    providers: [
        MatchingService,
        ClientService,
        ClientAreaService,
        PeriodService
    ]
})
export class CreateMatchingBatchComponent implements OnInit {
    public batchId: number = 0;
    public isSaving: boolean = false;
    public formSubmitted: boolean = false;
    public showClientAreaDropdown: boolean = false;

    public selectedPeriodId: number | undefined = undefined;
    public selectedClientId: number | undefined = undefined;
    public selectedClientAreaId: number | undefined = undefined;

    public periods: AllPeriodItem[] = [];
    public clients: { id: number; name: string }[] = [];
    public clientAreas: { id: number; name: string }[] = [];

    public selectedFile: File | null = null;
    public selectedFileName: string = '';

    constructor(
        private router: Router,
        private matchingService: MatchingService,
        private periodService: PeriodService,
        private clientService: ClientService,
        private clientAreaService: ClientAreaService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private initializerService: InitializerService,
    ) {}

    ngOnInit(): void {
        this.getPeriods();
        this.getClients();
    }


    getPeriods(): void {
        this.periodService.getAllPeriods()
            .subscribe((data) => {
                this.periods = data.results || [];
                this.selectedPeriodId = this.periods[1]?.id ?? 0;
            });
    }

    getClients(): void {
        this.clientService.getAllClients()
            .subscribe((data) => {
                this.clients = (data.results || []).map(c => ({
                    id: c.id,
                    name: c.clientName
                }));
            });
    }

    getClientAreasForClient(): void {
        if (!this.selectedClientId) {
            this.clientAreas = [];
            this.showClientAreaDropdown = false;
            return;
        }

        this.clientAreaService.getClientAreaLookup(this.selectedClientId)
            .subscribe({
                next: (clientAreas) => {
                    if (!clientAreas.length) {
                        this.toastService.show('No policies found for the selected client', 'info');
                        this.showClientAreaDropdown = false;
                        return;
                    }

                    this.clientAreas = clientAreas.map(ca => ({
                        id: ca.id,
                        name: ca.name
                    }));

                    this.showClientAreaDropdown = true;

                    if (this.clientAreas.length === 1) {
                        this.selectedClientAreaId = this.clientAreas[0].id;
                    }
                },
                error: () => {
                    this.toastService.show('Failed to load client areas', 'error');
                    this.showClientAreaDropdown = false;
                }
            });
    }

    displayPeriod(option: any): string {
        const paddedMonth = ('0' + option.month).slice(-2);
        return `${option.year}-${paddedMonth}`;
    }

    displayClientArea(option: any): string {
        return option?.name || 'Unnamed';
    }

    onPeriodSelectionChange(selectedId: number | null): void {
        this.selectedPeriodId = selectedId ?? undefined;
    }

    onClientSelectionChange(selectedId: number | null): void {
        this.selectedClientId = selectedId ?? undefined;
        this.selectedClientAreaId = undefined;
        this.clientAreas = [];
        this.showClientAreaDropdown = false;

        if (this.selectedClientId) {
            this.getClientAreasForClient();
        }
    }

    onClientAreaSelectionChange(selectedId: number | null): void {
        this.selectedClientAreaId = selectedId ?? undefined;
    }

    onFileSelected(event: any): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
            this.selectedFileName = input.files[0].name;
        }
        else {
            this.selectedFile = null;
            this.selectedFileName = '';
        }
    }

    isValid(): boolean {
        return !!this.selectedPeriodId && !!this.selectedClientId && !!this.selectedClientAreaId;
    }

    onSave(form: NgForm): void {
        this.formSubmitted = true;

        if (form.invalid || !this.selectedPeriodId || !this.selectedClientId || !this.selectedClientAreaId) {
            return;
        }

        this.isSaving = true;
        const payload = new CreateMatchingInput();
        payload.periodId = this.selectedPeriodId;
        payload.clientId = this.selectedClientId;
        payload.clientAreaId = this.selectedClientAreaId;

        this.matchingService.createMatchingBatch(payload)
            .subscribe({
                next: (result: MatchingCreateResult) => {
                    if (!result.isSourcesAvailable) {
                        this.toastService.show('No completed annexures/ageing records found for the period, client and client area', 'info');
                        this.isSaving = false;
                        return;
                    }

                    if (!result.isSourcesCompleted) {
                        this.toastService.show('Cannot create — a completed batch exists for this client, area and period. Delete it first.', 'info');
                        this.isSaving = false;
                        return;
                    }

                    this.isSaving = false;
                    this.toastService.show('Batch created', 'Success');

                    sessionStorage.setItem('isRedirected', 'true');

                    if (result && result.id) {
                        this.router.navigate([`/matching-wizard/${result.id}`], {
                            queryParams: {
                                clientId: this.selectedClientId,
                                clientAreaId: this.selectedClientAreaId,
                                periodId: this.selectedPeriodId
                            }
                        });
                    }
                    else {
                        this.toastService.show('Batch created, but no ID returned.', 'error');
                    }
                },
                error: (error) => {
                    this.isSaving = false;
                    this.toastService.show('Error creating batch.', 'Error');
                    this.errorService.handleError(error);
                }
            });
    }

    onCancel(): void {
        this.router.navigate([`/matching`]);
    }
}
